import { Box, Flex, SimpleGrid, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { crm } from "../../theme";
import { ContaOrdemParams, ContaOrdemResult, Regime, regimeLabels } from "./contaOrdem";

export interface EstimateHeader {
  number: string;
  client: string;
  product: string;
  origin: string;
  modal: string;
  incoterm: string;
  regime: Regime;
  equipment: string;
  date: Date;
}

export interface EstimateExpense {
  label: string;
  amountBrl: number;
}

export interface EstimateItemInfo {
  description: string;
  iiRate: number;
  ipiRate: number;
  pisRate: number;
  cofinsRate: number;
}

interface Props {
  header: EstimateHeader;
  result: ContaOrdemResult;
  items: EstimateItemInfo[];
  expenses: EstimateExpense[];
  params: ContaOrdemParams;
  currency: string;
  exchangeRate: number;
  /** Linhas internas (custo ICMS / honorário CVF / crédito presumido). Nunca na impressão. */
  showInternal?: boolean;
  /** Layout de impressão: cabeçalho da estimativa, sem sombra. */
  print?: boolean;
}

const number = (value: number, digits = 2) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const formatMoney = (value: number, currency: string) => {
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return safe.toLocaleString("pt-BR", { style: "currency", currency });
  } catch {
    return `${currency} ${number(safe)}`;
  }
};

const pct = (value?: number | null) =>
  value == null ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;

/** Alíquota só aparece quando todos os itens usam a mesma (senão varia por item). */
const uniform = (values: number[]) => (values.length && values.every((v) => v === values[0]) ? values[0] : null);

const cell = { px: "10px", py: "6px", fontSize: "13px", borderColor: crm.border } as const;

/** Espelho da aba "Estimativa" da planilha de conta e ordem. */
const EstimateSheet = ({ header, result, items, expenses, params, currency, exchangeRate, showInternal, print }: Props) => {
  const t = result.totals;
  const money = (v: number) => formatMoney(v, currency);
  const brl = (v: number) => formatMoney(v * exchangeRate, "BRL");
  const expenseTotalBrl = expenses.reduce((sum, e) => sum + e.amountBrl, 0);

  const Section = ({ title }: { title: string }) => (
    <Tr>
      <Td {...cell} colSpan={4} bg={crm.columnBg} fontWeight="700" fontSize="12px" letterSpacing="0.04em" color={crm.text}>
        {title}
      </Td>
    </Tr>
  );

  const Row = ({ label, rate, value, valueBrl, strong, highlight }: {
    label: string;
    rate?: string;
    value: number;
    /** R$ direto (despesas); senão value × taxa. */
    valueBrl?: number;
    strong?: boolean;
    highlight?: boolean;
  }) => (
    <Tr bg={highlight ? crm.brandSoft : undefined}>
      <Td {...cell} fontWeight={strong ? "700" : "400"}>{label}</Td>
      <Td {...cell} color={crm.textMuted} isNumeric>{rate ?? ""}</Td>
      <Td {...cell} isNumeric fontWeight={strong ? "700" : "400"}>{money(value)}</Td>
      <Td {...cell} isNumeric fontWeight={strong ? "700" : "400"} color={highlight ? crm.brandDark : undefined}>
        {valueBrl != null ? formatMoney(valueBrl, "BRL") : brl(value)}
      </Td>
    </Tr>
  );

  const UnitRows = ({ field }: { field: "disbursementPerUnit" | "finalCostPerUnit" }) => (
    <>
      {result.items.map((item, index) => (
        <Tr key={index}>
          <Td {...cell} pl="24px">{items[index]?.description || `Item ${index + 1}`}</Td>
          <Td {...cell} color={crm.textMuted} isNumeric>{number(item.quantity, 0)} un.</Td>
          <Td {...cell} isNumeric>{money(item[field])}</Td>
          <Td {...cell} isNumeric>{brl(item[field])}</Td>
        </Tr>
      ))}
    </>
  );

  const icmsTradingRate = params.cvfFeeRate + params.icmsCollectRate;
  const grossRate = (v: number) => number(v * exchangeRate);

  return (
    <Box>
      {print && (
        <Box mb="16px">
          <Text fontSize="18px" fontWeight="800" color={crm.text}>
            ESTIMATIVA DE CUSTOS DE IMPORTAÇÃO POR CONTA E ORDEM
          </Text>
          <SimpleGrid columns={2} spacingX="32px" spacingY="2px" mt="10px" fontSize="13px">
            <Text><b>CVF Nº:</b> {header.number || "—"}</Text>
            <Text><b>Regime tributário:</b> {regimeLabels[header.regime]}</Text>
            <Text><b>Cliente:</b> {header.client || "—"}</Text>
            <Text><b>Total FOB:</b> {money(t.fob)}</Text>
            <Text><b>Produto:</b> {header.product || "—"}</Text>
            <Text><b>Quantidade:</b> {number(result.quantity, 0)}</Text>
            <Text><b>Origem:</b> {header.origin || "—"}</Text>
            <Text><b>Equipamento:</b> {header.equipment || "—"}</Text>
            <Text><b>Modal:</b> {header.modal}</Text>
            <Text><b>Data da estimativa:</b> {header.date.toLocaleDateString("pt-BR")}</Text>
            <Text><b>Incoterm:</b> {header.incoterm || "—"}</Text>
            <Text><b>Taxa fiscal {currency}:</b> {number(exchangeRate, 4)}</Text>
          </SimpleGrid>
        </Box>
      )}

      <Box border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm} overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th {...cell} fontSize="11px" />
              <Th {...cell} fontSize="11px" isNumeric>Alíquota</Th>
              <Th {...cell} fontSize="11px" isNumeric>Operação em {currency}</Th>
              <Th {...cell} fontSize="11px" isNumeric>Operação em R$</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Row label="Valor FOB" value={t.fob} />
            <Row label="Seguro" rate={pct(params.insuranceRate)} value={t.insurance} />
            <Row label="Valor do frete internacional" value={t.freight} />
            <Row label="VALOR CIF" value={t.cif} strong />

            <Section title="TRIBUTOS" />
            <Row label="I.I." rate={pct(uniform(items.map((i) => i.iiRate)))} value={t.ii} />
            <Row label="I.P.I." rate={pct(uniform(items.map((i) => i.ipiRate)))} value={t.ipi} />
            <Row label="ICMS na importação via trading" rate={pct(icmsTradingRate)} value={t.icms} />
            <Row label="PIS" rate={pct(uniform(items.map((i) => i.pisRate)))} value={t.pis} />
            <Row label="COFINS" rate={pct(uniform(items.map((i) => i.cofinsRate)))} value={t.cofins} />
            <Row label="Dumping" value={t.dumping} />
            <Row label="TOTAL DE TRIBUTOS" value={t.taxes} strong />

            <Section title="DESPESAS ADUANEIRAS ESTIMADAS" />
            {expenses.map((e) => (
              <Row key={e.label} label={e.label} value={exchangeRate > 0 ? e.amountBrl / exchangeRate : 0} valueBrl={e.amountBrl} />
            ))}
            <Row label="TOTAL DE DESPESAS ADUANEIRAS" value={t.expenses} valueBrl={expenseTotalBrl} strong />

            <Row label="DIFERENÇA IPI" value={t.ipiDifference} />
            <Row label="TOTAL DE DESEMBOLSO NA IMPORTAÇÃO" value={t.disbursement} strong highlight />

            <Section title="DESEMBOLSO POR UNIDADE" />
            <UnitRows field="disbursementPerUnit" />

            <Section title="CRÉDITOS" />
            <Row label="Crédito ICMS" rate={pct(params.icmsCreditRate)} value={t.icmsCredit} />
            <Row label="Crédito I.P.I." value={t.ipiCredit} />
            <Row label="Crédito PIS" rate={header.regime === "REAL" ? pct(params.pisCreditRate) : "—"} value={t.pisCredit} />
            <Row label="Crédito COFINS" rate={header.regime === "REAL" ? pct(params.cofinsCreditRate) : "—"} value={t.cofinsCredit} />
            <Row label="CUSTO FINAL DA IMPORTAÇÃO CONSIDERANDO CRÉDITOS" value={t.finalCost} strong highlight />

            <Section title="PREÇO CUSTO NET POR UNIDADE" />
            <UnitRows field="finalCostPerUnit" />
          </Tbody>
        </Table>
      </Box>

      <Text fontSize="12px" color={crm.textMuted} mt="8px">
        Validade da estimativa: 7 dias.
      </Text>

      {showInternal && !print && (
        <Box mt="16px" p="14px 16px" borderRadius={crm.radiusSm} border={`1px dashed ${crm.borderStrong}`} bg={crm.subtle}>
          <Text fontSize="12px" fontWeight="700" color={crm.textMuted} mb="6px">
            USO INTERNO · não sai na impressão
          </Text>
          <Flex gap="32px" wrap="wrap" fontSize="13px">
            <Text>Custo ICMS ({pct(params.icmsCollectRate)}): <b>R$ {grossRate(t.icmsCost)}</b></Text>
            <Text>Honorário CVF: <b>R$ {grossRate(t.cvfFee)}</b></Text>
            <Text>Crédito presumido: <b>R$ {grossRate(t.presumedCredit)}</b></Text>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default EstimateSheet;
